import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { create } from 'zustand';
import { type ApiUser, api, type OnboardingRecord, type Product } from '../api';
import {
  useAzureDevOpsConnection,
  useMicrosoftConnectionStatus,
  useTranscriptionAvailable,
} from '../hooks/queries';
import { type TranslationKey, useT } from '../i18n';
import { dictationSupported } from '../lib/useDictation';
import { firstNameFromEmail } from '../lib/userName';
import { MenuIcon } from './MenuIcon';

/** Raise when the tour changes enough that everyone should see the welcome again. */
export const CHAT_TOUR_VERSION = 1;

/** Started from the welcome or from Settings; ChatTour (mounted once in App) shows it. */
export const useChatTour = create<{ active: boolean; start: () => void; stop: () => void }>(
  (set) => ({
    active: false,
    start: () => set({ active: true }),
    stop: () => set({ active: false }),
  }),
);

export function useOnboardingRecord() {
  return useQuery({
    queryKey: ['onboarding'],
    queryFn: () => api.onboarding(),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export type TourStep = {
  id: string;
  /** `data-tour` values; the first one on screen is highlighted. */
  targets: string[];
  title: TranslationKey;
  body: TranslationKey;
};

/** The steps this user can follow: a step whose feature is off or not allowed is left out. */
export function chatTourSteps(input: {
  product: Product;
  agentsEnabled: boolean;
  dictation: boolean;
  /** False when Microsoft 365 cannot be connected here and no DevOps token is allowed. */
  accounts: boolean;
  azureDevOps: boolean;
}): TourStep[] {
  const { capabilities, profile } = input.product;
  const full = profile !== 'light';
  const steps: Array<TourStep | false> = [
    { id: 'ask', targets: ['composer'], title: 'tourAskTitle', body: 'tourAskBody' },
    full &&
      (capabilities.knowledgeSelection || capabilities.skills) && {
        id: 'data',
        targets: ['context'],
        title: 'tourDataTitle',
        body: 'tourDataBody',
      },
    input.dictation
      ? { id: 'files', targets: ['dictate'], title: 'tourFilesTitle', body: 'tourFilesBody' }
      : {
          id: 'files',
          targets: ['context'],
          title: 'tourFilesOnlyTitle',
          body: 'tourFilesOnlyBody',
        },
    full &&
      capabilities.projects && {
        id: 'projects',
        targets: ['nav-projects'],
        title: 'tourProjectsTitle',
        body: 'tourProjectsBody',
      },
    full &&
      ((capabilities.agents && input.agentsEnabled) || capabilities.automations) && {
        id: 'agents',
        targets: ['nav-agents', 'nav-tasks'],
        title: 'tourAgentsTitle',
        body: 'tourAgentsBody',
      },
    capabilities.externalIntegrations &&
      input.accounts && {
        id: 'accounts',
        targets: ['account'],
        title: 'tourAccountsTitle',
        body: input.azureDevOps ? 'tourAccountsBodyDevOps' : 'tourAccountsBody',
      },
  ];
  return steps.filter((step): step is TourStep => step !== false);
}

/** Another dialog, sheet or popover is open: the welcome waits until it closes. */
function otherDialogOpen(): boolean {
  return document.querySelector('[role="dialog"], [role="alertdialog"]') !== null;
}

function reducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}

/** Welcome on first sign-in and the coach-mark tour. Mount once, inside the signed-in shell. */
export function ChatTour({ user, product }: { user: ApiUser; product: Product }) {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const active = useChatTour((s) => s.active);
  const record = useOnboardingRecord();
  const save = useMutation({
    mutationFn: (status: OnboardingRecord['status']) =>
      api.recordOnboarding({ version: CHAT_TOUR_VERSION, status }),
    onSuccess: (data) => queryClient.setQueryData(['onboarding'], data),
  });
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const welcomeHandled = useRef(false);
  const due = record.isSuccess && (record.data === null || record.data.version < CHAT_TOUR_VERSION);
  // Returning from the Microsoft sign-in is someone else's moment; wait for the next visit.
  const returningFromConnect = new URLSearchParams(location.search).has('microsoft');

  useEffect(() => {
    if (!due || active || welcomeHandled.current || returningFromConnect) return;
    const open = () => {
      if (otherDialogOpen()) return false;
      welcomeHandled.current = true;
      setWelcomeOpen(true);
      return true;
    };
    if (open()) return;
    const timer = window.setInterval(() => {
      if (open()) window.clearInterval(timer);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [due, active, returningFromConnect]);

  const shown = welcomeOpen || active;
  const transcription = useTranscriptionAvailable();
  const microsoft = useMicrosoftConnectionStatus(
    shown && product.capabilities.externalIntegrations,
  );
  const devOps = useAzureDevOpsConnection(shown && product.capabilities.externalIntegrations);
  const azureDevOps =
    devOps.data?.configured === true &&
    devOps.data.enabled &&
    devOps.data.personalAccessTokenAllowed;
  const steps = chatTourSteps({
    product,
    agentsEnabled: user.agentsEnabled,
    dictation: transcription && dictationSupported(),
    accounts: microsoft.data?.delegatedEnabled !== false || azureDevOps,
    azureDevOps,
  });

  const finish = (status: OnboardingRecord['status']) => {
    useChatTour.getState().stop();
    // Skipping a tour taken again keeps the earlier result; finishing always records.
    const current = queryClient.getQueryData<OnboardingRecord | null>(['onboarding']);
    if (status === 'completed' || !current || current.version < CHAT_TOUR_VERSION) {
      save.mutate(status);
    }
  };

  const name = user.firstName || firstNameFromEmail(user.email);
  return (
    <>
      <Dialog.Root
        open={welcomeOpen}
        onOpenChange={(open) => {
          if (open) return;
          setWelcomeOpen(false);
          finish('skipped');
        }}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="nv-confirm-overlay" />
          <Dialog.Content className="nv-root nv-confirm nv-tour-welcome">
            <span className="nv-tour-icon" aria-hidden="true">
              <MenuIcon name="sparkles" />
            </span>
            <Dialog.Title className="nv-confirm-title">
              {name ? t('tourWelcomeTitle').replace('{name}', name) : t('tourWelcomeTitleNoName')}
            </Dialog.Title>
            <Dialog.Description className="nv-confirm-text">
              {t('tourWelcomeText').replace('{count}', String(steps.length))}
            </Dialog.Description>
            <div className="nv-confirm-actions">
              <Dialog.Close className="btn-secondary">{t('tourLater')}</Dialog.Close>
              <button
                type="button"
                className="btn-primary"
                onClick={() => {
                  setWelcomeOpen(false);
                  useChatTour.getState().start();
                }}
              >
                {t('tourStart')}
              </button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
      {active && (
        <CoachMarks
          steps={steps}
          atHome={location.pathname === '/'}
          goHome={() => navigate('/')}
          onClose={finish}
        />
      )}
    </>
  );
}

type Box = { top: number; left: number; width: number; height: number };
type Placement = { ring: Box | null; card: { top: number; left: number } | null };

const GAP = 12;
const MARGIN = 16;
const RING_PAD = 6;

function findTarget(targets: string[]): HTMLElement | null {
  for (const name of targets) {
    for (const el of document.querySelectorAll<HTMLElement>(`[data-tour="${name}"]`)) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  }
  return null;
}

function onScreen(r: DOMRect): boolean {
  return r.bottom > 0 && r.right > 0 && r.top < window.innerHeight && r.left < window.innerWidth;
}

/** Card beside the target: right of a narrow left-hand item, else below, else above. */
function placeCard(r: DOMRect, card: { width: number; height: number }) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const clampX = (x: number) => Math.min(Math.max(x, MARGIN), vw - card.width - MARGIN);
  const clampY = (y: number) => Math.min(Math.max(y, MARGIN), vh - card.height - MARGIN);
  if (r.right < vw * 0.4 && r.right + GAP + card.width + MARGIN <= vw) {
    return { left: r.right + GAP + RING_PAD, top: clampY(r.top + r.height / 2 - card.height / 2) };
  }
  const left = clampX(r.left + r.width / 2 - card.width / 2);
  if (r.bottom + GAP + RING_PAD + card.height + MARGIN <= vh) {
    return { left, top: r.bottom + GAP + RING_PAD };
  }
  return { left, top: clampY(r.top - GAP - RING_PAD - card.height) };
}

function CoachMarks({
  steps,
  atHome,
  goHome,
  onClose,
}: {
  steps: TourStep[];
  atHome: boolean;
  goHome: () => void;
  onClose: (status: OnboardingRecord['status']) => void;
}) {
  const t = useT();
  const [index, setIndex] = useState(0);
  const [place, setPlace] = useState<Placement>({ ring: null, card: null });
  const card = useRef<HTMLDivElement | null>(null);
  const returnFocus = useRef<Element | null>(null);
  const scrolledFor = useRef<string | null>(null);
  const current = Math.min(index, steps.length - 1);
  const step = steps[current];
  const last = current === steps.length - 1;

  // The steps live on the home page and the sidebar.
  useEffect(() => {
    if (!atHome) goHome();
  }, [atHome, goHome]);

  useEffect(() => {
    returnFocus.current = document.activeElement;
    return () => {
      const previous = returnFocus.current;
      const fallback = document.querySelector<HTMLElement>('[data-tour="composer"] textarea');
      if (previous instanceof HTMLElement && previous.isConnected && previous !== document.body) {
        previous.focus();
      } else {
        fallback?.focus();
      }
    };
  }, []);

  const measure = useCallback(() => {
    if (!step) return;
    const node = card.current;
    const size = { width: node?.offsetWidth ?? 340, height: node?.offsetHeight ?? 200 };
    const target = findTarget(step.targets);
    if (target && scrolledFor.current !== step.id) {
      scrolledFor.current = step.id;
      target.scrollIntoView?.({
        block: 'nearest',
        inline: 'nearest',
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }
    const r = target?.getBoundingClientRect();
    const nextPlace: Placement =
      r && onScreen(r)
        ? {
            ring: {
              top: r.top - RING_PAD,
              left: r.left - RING_PAD,
              width: r.width + RING_PAD * 2,
              height: r.height + RING_PAD * 2,
            },
            card: placeCard(r, size),
          }
        : { ring: null, card: null };
    setPlace((prev) => (JSON.stringify(prev) === JSON.stringify(nextPlace) ? prev : nextPlace));
  }, [step]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  // Follow the target on resize, scroll and late rendering (the home page may still be loading).
  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    const timer = window.setInterval(measure, 500);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('resize', schedule);
      window.removeEventListener('scroll', schedule, true);
      window.clearInterval(timer);
    };
  }, [measure]);

  // Focus moves into the card on every step, so screen readers read the new step.
  useEffect(() => {
    card.current?.focus();
  }, [current]);

  if (!step) return null;

  const next = () => (last ? onClose('completed') : setIndex(current + 1));
  const previous = () => setIndex(Math.max(current - 1, 0));
  const onKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      onClose('skipped');
    } else if (
      event.key === 'ArrowRight' ||
      (event.key === 'Enter' && event.target === card.current)
    ) {
      event.preventDefault();
      next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      previous();
    } else if (event.key === 'Tab') {
      // Keep focus inside the card while the tour is open.
      const focusable = card.current?.querySelectorAll<HTMLElement>('button:not([disabled])');
      const first = focusable?.[0];
      const lastButton = focusable?.[focusable.length - 1];
      if (!first || !lastButton) return;
      if (event.shiftKey && (document.activeElement === first || event.target === card.current)) {
        event.preventDefault();
        lastButton.focus();
      } else if (!event.shiftKey && document.activeElement === lastButton) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  const titleId = `nv-tour-title-${step.id}`;
  const bodyId = `nv-tour-body-${step.id}`;
  return createPortal(
    <div className="nv-root nv-tour">
      {/* Blocks the page while the tour is open; the ring dims everything but the target. */}
      <div className="nv-tour-shield" data-dim={place.ring ? undefined : 'true'} />
      {place.ring && <div className="nv-tour-ring" aria-hidden="true" style={place.ring} />}
      <div
        ref={card}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        tabIndex={-1}
        className="nv-tour-card"
        data-centered={place.card ? undefined : 'true'}
        style={place.card ?? undefined}
        onKeyDown={onKeyDown}
      >
        <p className="nv-tour-count">
          {t('tourStepOf')
            .replace('{current}', String(current + 1))
            .replace('{total}', String(steps.length))}
        </p>
        <h2 id={titleId} className="nv-tour-title">
          {t(step.title)}
        </h2>
        <p id={bodyId} className="nv-tour-body">
          {t(step.body)}
        </p>
        <div className="nv-tour-foot">
          <span className="nv-tour-dots" aria-hidden="true">
            {steps.map((item, i) => (
              <span key={item.id} data-on={i === current ? 'true' : undefined} />
            ))}
          </span>
          <button type="button" className="nv-tour-skip" onClick={() => onClose('skipped')}>
            {t('tourSkip')}
          </button>
          {current > 0 && (
            <button type="button" className="btn-secondary" onClick={previous}>
              {t('tourPrevious')}
            </button>
          )}
          <button type="button" className="btn-primary" onClick={next}>
            {last ? t('tourDone') : t('tourNext')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
